import type { TFunction } from 'i18next';
import React from 'react';

import { Grid } from '../../../common/components/Grid/Grid';
import {
  GridColumn,
  GridCountFunction,
  GridGetterFunction,
} from '../../../common/components/Grid/GridDataTypes';
import { GetApplicationUnblockDto } from '../../../common/pokCore/autogenerated/pokApiClient';
import {
  ApplicationUnblockStatusObj,
  ApplicationUnblockTypeObj,
} from '../../../common/pokCore/validation/enums';
import momencik from '../../../common/momencik';
import { buildProjectLabel } from '../../../utils/buildProjectLabel';
import { useTranslation } from '../../../common/hooks/useTranslation';

interface ApplicationUnblockSearchProps {
  getData?: GridGetterFunction;
  getCount?: GridCountFunction;
  onRowClick?: (key: string) => Promise<void> | void | undefined;
  refresh?: boolean;
  isNavView?: boolean;
  showFilter?: boolean;
  emptyGridMessage?: string;
}

export const applicationUnblockForGrid = (
  t: TFunction<'applicationLng', 'applicationLng'>,
  applicationUnblock: GetApplicationUnblockDto,
) => {
  return {
    key: applicationUnblock.id,
    values: {
      'applicationUnblock.systemNumber': applicationUnblock.systemNumber,
      'applicationUnblock.type': t(
        ApplicationUnblockTypeObj[applicationUnblock.type],
      ),
      'applicationUnblock.description': applicationUnblock.description,
      'project.name': buildProjectLabel(
        applicationUnblock.estimateByMonth.project,
      ),
      'profile.name': applicationUnblock.estimateByMonth.profile.name,
      'estimateByMonth.date': momencik(
        applicationUnblock.estimateByMonth.date,
        'YYYY-MM',
      ),
      'applicationUnblock.status': t(
        ApplicationUnblockStatusObj[applicationUnblock.status],
      ),
      'applicationUnblock.rejectedNote': applicationUnblock.rejectedNote,
      'creator.name': applicationUnblock.creator.name,
    },
    isInactive: !applicationUnblock.active,
  };
};

export const ApplicationUnblockSearch: React.FC<
  ApplicationUnblockSearchProps
> = props => {
  const { t, tk } = useTranslation('applicationLng');

  const columns: GridColumn[] = [
    {
      header: t(tk.common.sysNumber),
      property: 'applicationUnblock.systemNumber',
    },
    { header: t(tk.app.type), property: 'applicationUnblock.type' },
    ...(props.isNavView
      ? [
          { header: t(tk.prLang.project), property: 'project.name' },
          { header: t(tk.common.month), property: 'estimateByMonth.date' },
          { header: t(tk.common.profile), property: 'profile.name' },
        ]
      : []),
    { header: t(tk.app.reason), property: 'applicationUnblock.description' },
    { header: t(tk.common.status), property: 'applicationUnblock.status' },
    {
      header: t(tk.app.rejectedNote),
      property: 'applicationUnblock.rejectedNote',
    },
    { header: t(tk.common.creator), property: 'creator.name' },
  ];

  return (
    <Grid
      getData={props.getData}
      getDataCount={props.getCount}
      columns={columns}
      refresh={props.refresh}
      initialOrderColumn="applicationUnblock.systemNumber"
      initialOrderDirection="DESC"
      availablePageSizes={[10, 20, 50]}
      emptyGridMessage={props.emptyGridMessage || t(tk.app.noUnblock)}
      onRowClick={props.onRowClick}
      withHistory={true}
      showFilter={props.showFilter}
    />
  );
};
